import { includes, invert } from 'lodash-es';
import pino from 'pino';

type Values<T> = T extends Record<string, infer P> ? P : never;
const defaultLogLevelMap = {
  error: 3,
  warn: 4,
  info: 6,
  debug: 7,
} as const;

export { defaultLogLevelMap };
export type { Values };

type LogLevel = typeof defaultLogLevelMap;

class Logger {
  protected logger: ReturnType<typeof pino>;
  /**
   * 서버와 log level을 동기화 하기 위한 설정
   * @see {@link https://github.com/winstonjs/winston#logging-levels Winston log level}
   */
  private readonly defaultLogLevelMap: LogLevel;

  constructor() {
    this.defaultLogLevelMap = defaultLogLevelMap;

    this.logger = pino();

    this.logger.levels = {
      labels: invert(this.defaultLogLevelMap),
      values: this.defaultLogLevelMap,
    };
  }

  public debug(message: unknown | undefined) {
    this.log('debug', message);
  }

  public info(message: unknown | undefined) {
    this.log('info', message);
  }

  public warn(message: unknown | undefined) {
    this.log('warn', message);
  }

  public error(message: unknown | undefined) {
    this.log('error', message);
  }

  public log(
    logLevel: keyof typeof defaultLogLevelMap | Values<typeof defaultLogLevelMap>,
    message: unknown | undefined,
  ) {
    if (this.isLogLevelKey(logLevel)) {
      this.logger[logLevel](message);

      return;
    }

    const loggerType = this.findLoggerTypeForSyslogLevel(logLevel);

    if (loggerType) {
      this.logger[loggerType](message);
    }
  }

  private isLogLevelKey(level: unknown): level is keyof typeof this.defaultLogLevelMap {
    return typeof level === 'string' && includes(Object.keys(this.defaultLogLevelMap), level);
  }

  private findLoggerTypeForSyslogLevel(syslogLevel: number) {
    const logLevelCandidate = this.logger.levels.labels[syslogLevel];

    return this.isLogLevelKey(logLevelCandidate) ? logLevelCandidate : null;
  }
}

const logger = new Logger();

export { logger };

export default Logger;
